import React from 'react';
import CopyBlock from '../worklog/CopyBlock';
import Callout from './Callout';
import { inlineCode, ModeSwitch, pageTitle, type InstallMode } from './ui';

const HOOK_SETTINGS = `{
  "hooks": {
    "Stop": [
      {
        "hooks": [
          {
            "type": "command",
            "command": "$HOME/.claude/hooks/worklog-stop.sh",
            "timeout": 10
          }
        ]
      }
    ]
  }
}`;

// The four manual steps. script is null when the deploy didn't ship skills/.
const ManualSteps: React.FC<{ script: string | null }> = ({ script }) => (
  <ol className="list-decimal space-y-4 pl-5 text-sm text-gray-700">
    <li>
      <strong>确认有 jq。</strong>脚本用它解析 Claude Code 传进来的 JSON 才拿得到{' '}
      <code className={inlineCode}>prompt_id</code>。macOS 不预装 —— 没有的话脚本会静默退出，看起来像「装好了但从来不触发」。
      <div className="mt-1.5">
        <CopyBlock code="jq --version" />
      </div>
      <p className="mt-1 text-xs text-gray-500">
        没有就 <code className={inlineCode}>brew install jq</code>。
      </p>
    </li>
    <li>
      <strong>存脚本</strong>，存成 <code className={inlineCode}>~/.claude/hooks/worklog-stop.sh</code>：
      <div className="mt-1.5 space-y-1.5">
        <CopyBlock code="mkdir -p ~/.claude/hooks" />
        {script ? (
          <CopyBlock code={script} clampLines={4} />
        ) : (
          <Callout tone="warn" title="hook 脚本没随部署打包">
            服务端读不到 skills/worklog/worklog-stop.sh。请从仓库的 skills/worklog/worklog-stop.sh 取，或检查部署是否包含
            skills/ 目录。
          </Callout>
        )}
      </div>
    </li>
    <li>
      <strong>给执行权限。</strong>
      <div className="mt-1.5">
        <CopyBlock code="chmod +x ~/.claude/hooks/worklog-stop.sh" />
      </div>
    </li>
    <li>
      <strong>
        并进 <code className={inlineCode}>~/.claude/settings.json</code>。
      </strong>
      文件已存在时把 <code className={inlineCode}>hooks</code> 并进顶层，别整体覆盖。
      <div className="mt-1.5">
        <CopyBlock code={HOOK_SETTINGS} />
      </div>
    </li>
  </ol>
);

type Props = {
  // skills/worklog/worklog-stop.sh as read by the server; null when the deploy
  // didn't ship skills/.
  script: string | null;
  // HookInstallPrompt's text; null exactly when script is.
  prompt: string | null;
};

// /developers/worklog-hook: the optional Stop hook for Claude Code, installed
// either by pasting a prompt into Claude Code or by hand.
const HookTopic: React.FC<Props> = ({ script, prompt }) => {
  const [mode, setMode] = React.useState<InstallMode>(prompt ? 'ai' : 'manual');

  return (
    <>
      <h1 className={pageTitle}>Worklog skills</h1>
      <p className="mt-1.5 text-xs text-gray-500">Stop hook · 只对 Claude Code（含 Desktop 的 Code tab）</p>
      <p className="mt-4 text-sm text-gray-700">
        接上之后 AI <strong>能</strong>写工作日志，但不一定<strong>会</strong>写 —— 调不调工具是模型自己判断，实际会漏。
        加一个 Stop hook，它在每轮结束时拦一次，要求模型记一条。确实没有实质性工作的回合，它会说明理由而不是硬造。
      </p>

      <div className="mt-4 space-y-2.5 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        {prompt && <ModeSwitch mode={mode} onChange={setMode} />}
        {mode === 'ai' && prompt ? (
          <>
            <p className="text-sm text-gray-700">
              把这段整个发给 Claude Code。它会先确认有 jq，再写好脚本、并进 settings.json，不会动你别的配置。
            </p>
            <CopyBlock code={prompt} clampLines={6} />
            <p className="text-xs text-gray-500">hook 是会话启动时读的，装完新开一个会话才生效。</p>
          </>
        ) : (
          <ManualSteps script={script} />
        )}
      </div>

      <div className="mt-4">
        <Callout tone="info" title="它不会把你卡死">
          Stop hook 无条件拦截会造成死循环。这份脚本有三重兜底：<code className={inlineCode}>stop_hook_active</code>{' '}
          为真就放过、按 <code className={inlineCode}>prompt_id</code> 的标记文件保证每个回合只拦一次、拿不到{' '}
          <code className={inlineCode}>prompt_id</code> 直接放过。每回合最多多走一轮。不想要了就删掉 settings.json 里的{' '}
          <code className={inlineCode}>hooks</code> 键。
        </Callout>
      </div>
    </>
  );
};

export default HookTopic;
